import { NoteTagConfig, NotesSettings } from '../types';

const TAGS_CONFIG_KEY = 'notes-tags-config';
const NOTES_SETTINGS_KEY = 'notes-settings';

// Default tag configurations
const DEFAULT_TAGS: NoteTagConfig[] = [
  {
    id: 'consultation',
    name: 'consultation',
    label: 'Consultation',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
    isActive: true,
    order: 1,
    createdBy: 'System',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'goals',
    name: 'goals',
    label: 'Goals',
    color: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
    isActive: true,
    order: 2,
    createdBy: 'System',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'priorities',
    name: 'priorities',
    label: 'Priorities',
    color: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300',
    isActive: true,
    order: 3,
    createdBy: 'System',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'documents',
    name: 'documents',
    label: 'Documents',
    color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300',
    isActive: true,
    order: 4,
    createdBy: 'System',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'follow-up',
    name: 'follow-up',
    label: 'Follow-up',
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300',
    isActive: true,
    order: 5,
    createdBy: 'System',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'general',
    name: 'general',
    label: 'General',
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    isActive: true,
    order: 6,
    createdBy: 'System',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
];

// Default settings
const DEFAULT_SETTINGS: NotesSettings = {
  id: 'default-settings',
  allowCustomTags: true,
  requireTags: true,
  maxTitleLength: 100,
  maxContentLength: 2000,
  defaultTag: 'general',
  enableNotifications: false,
  autoSaveInterval: 5,
  retentionPolicy: 0,
  departments: ['all', 'credit_management', 'cybersweep', 'credit_block'],
  updatedBy: 'System',
  updatedAt: new Date().toISOString()
};

export const notesConfigStorage = {
  // Tag Configuration Methods
  getAllTags(): NoteTagConfig[] {
    try {
      const data = localStorage.getItem(TAGS_CONFIG_KEY);
      if (!data) {
        // Initialize with default tags if none exist
        this.initializeDefaultTags();
        return DEFAULT_TAGS;
      }
      return JSON.parse(data);
    } catch (error) {
      console.error('Error reading tag config from localStorage:', error);
      return DEFAULT_TAGS;
    }
  },

  getActiveTags(): NoteTagConfig[] {
    return this.getAllTags()
      .filter(tag => tag.isActive)
      .sort((a, b) => a.order - b.order);
  },

  saveTag(tag: NoteTagConfig): void {
    try {
      const allTags = this.getAllTags();
      const existingIndex = allTags.findIndex(t => t.id === tag.id);

      if (existingIndex >= 0) {
        allTags[existingIndex] = { ...tag, updatedAt: new Date().toISOString() };
      } else {
        allTags.push({ ...tag, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
      }

      localStorage.setItem(TAGS_CONFIG_KEY, JSON.stringify(allTags));
    } catch (error) {
      console.error('Error saving tag config:', error);
      throw new Error('Failed to save tag configuration');
    }
  },

  updateTag(tag: NoteTagConfig): void {
    try {
      const allTags = this.getAllTags();
      const index = allTags.findIndex(t => t.id === tag.id);

      if (index === -1) {
        throw new Error('Tag not found');
      }

      allTags[index] = { ...tag, updatedAt: new Date().toISOString() };
      localStorage.setItem(TAGS_CONFIG_KEY, JSON.stringify(allTags));
    } catch (error) {
      console.error('Error updating tag config:', error);
      throw new Error('Failed to update tag configuration');
    }
  },

  deleteTag(tagId: string): void {
    try {
      const allTags = this.getAllTags();
      const filteredTags = allTags.filter(tag => tag.id !== tagId);
      localStorage.setItem(TAGS_CONFIG_KEY, JSON.stringify(filteredTags));
    } catch (error) {
      console.error('Error deleting tag config:', error);
      throw new Error('Failed to delete tag configuration');
    }
  },

  initializeDefaultTags(): void {
    localStorage.setItem(TAGS_CONFIG_KEY, JSON.stringify(DEFAULT_TAGS));
  },

  // Settings Methods
  getSettings(): NotesSettings {
    try {
      const data = localStorage.getItem(NOTES_SETTINGS_KEY);
      if (!data) {
        // Initialize with default settings if none exist
        this.initializeDefaultSettings();
        return DEFAULT_SETTINGS;
      }
      return JSON.parse(data);
    } catch (error) {
      console.error('Error reading settings from localStorage:', error);
      return DEFAULT_SETTINGS;
    }
  },

  saveSettings(settings: NotesSettings): void {
    try {
      const updatedSettings = { ...settings, updatedAt: new Date().toISOString() };
      localStorage.setItem(NOTES_SETTINGS_KEY, JSON.stringify(updatedSettings));
    } catch (error) {
      console.error('Error saving settings:', error);
      throw new Error('Failed to save settings');
    }
  },

  initializeDefaultSettings(): void {
    localStorage.setItem(NOTES_SETTINGS_KEY, JSON.stringify(DEFAULT_SETTINGS));
  },

  // Utility Methods
  getTagByName(name: string): NoteTagConfig | undefined {
    const tags = this.getAllTags();
    return tags.find(tag => tag.name === name && tag.isActive);
  },

  getTagColor(tagName: string): string {
    const tag = this.getTagByName(tagName);
    return tag ? tag.color : DEFAULT_TAGS.find(t => t.name === 'general')?.color || 'bg-gray-100 text-gray-800';
  },

  getTagLabel(tagName: string): string {
    const tag = this.getTagByName(tagName);
    return tag ? tag.label : tagName;
  },

  // Reset to defaults (admin function)
  resetToDefaults(): void {
    this.initializeDefaultTags();
    this.initializeDefaultSettings();
  }
};